import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { t } from "../locale";
import { frameParentOrigin } from "./embed-session";
import { initializeAnalytics, resetAnalytics } from "../portal/analytics";
import { deviceRequest, ensureDeviceAccount, resetDeviceAccount, ViewerError } from "./device-session";
import type { LoginReason, LoginResult } from "./AccountLogin";
import { changeLocalItem, useLocalLibrary } from "./local-library";
import InstallGuide, { getInstallContext, isHomeScreen, type InstallReason } from "../portal/InstallGuide";
import { dismissInstallForSession, shouldOfferInstall } from "../portal/InstallReminder";
import { track } from "../portal/analytics";
let loginModule: Promise<typeof import("./AccountLogin")> | undefined;
function loadLogin() {
  return loginModule ??= import("./AccountLogin").catch(error => { loginModule = undefined; throw error; });
}
export type Viewer = {
  userId: string;
  accountType: "device" | "username" | "email" | "registered";
  registered: boolean;
  email?: string;
  username?: string;
  emailVerified?: boolean;
  deviceId: string;
  devicePlatform: string;
  nickname: string;
  avatar: string;
  enabled: boolean;
  revision: number;
  createdAt: string;
  lastSeenAt?: string;
};
export type LibraryItem = {
  seriesId: string;
  title: string;
  saved: boolean;
  progress: { episode: number; seconds: number; updatedAt: number } | null;
};
type Change = {
  seriesId: string;
  title: string;
  saved?: boolean;
  progress?: { episode: number; seconds: number } | null;
};
type ViewerContextType = {
  user: Viewer | null;
  ready: boolean;
  error: string;
  guestId: string;
  items: LibraryItem[];
  refresh: () => Promise<void>;
  updateProfile: (nickname: string, avatar: string) => Promise<void>;
  changeItem: (change: Change) => Promise<void>;
  requestLogin: (reason?: LoginReason) => Promise<boolean>;
  requestInstall: (reason?: InstallReason) => Promise<void>;
  offerRegistration: () => void;
  logout: () => Promise<void>;
};
const Context = createContext<ViewerContextType | null>(null);
export const useViewer = () => {
  const value = useContext(Context);
  if (!value) throw new Error("ViewerProvider required");
  return value;
};
export function ViewerProvider({ children, publicContent = false }: { children: ReactNode; publicContent?: boolean }) {
  const [user, setUser] = useState<Viewer | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const items = useLocalLibrary();
  const [installOpen, setInstallOpen] = useState(false);
  const [installReason, setInstallReason] = useState<InstallReason>("manual");
  const installActive = useRef(false), installIntentHandled = useRef(false);
  const installDialog = useRef<HTMLDialogElement>(null);
  const installWaiters = useRef<(() => void)[]>([]);
  const embeddedInstall = useRef<string | null>(null);
  const [loginReason, setLoginReason] = useState<LoginReason | null>(null);
  const [LoginPanel, setLoginPanel] = useState<typeof import("./AccountLogin")["default"] | null>(null);
  const [SuccessPanel, setSuccessPanel] = useState<typeof import("./PostRegistration")["default"] | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [registeredNotice, setRegisteredNotice] = useState(false);
  const loginWaiters = useRef<((value: boolean) => void)[]>([]);
  const embeddedLogin = useRef<string | null>(null);
  const currentProfile = useRef<Viewer | null>(null);
  const authChannel = useRef<BroadcastChannel | null>(null);
  const offered = useRef(false);
  const mounted = useRef(false);
  const refreshTask = useRef<Promise<void> | null>(null);
  function fail(error: unknown) {
    currentProfile.current = null;
    setError(
      error instanceof Error
        ? error.message
        : t("Could not connect to your account."),
    );
    setUser(null);
  }
  function refresh(recovered = false, initial = false): Promise<void> {
    if (refreshTask.current) return refreshTask.current;
    refreshTask.current = (async () => {
      try {
        const account = await ensureDeviceAccount();
        const profile = initial ? account : await deviceRequest<Viewer>("account/me");
        if (!mounted.current) return;
        currentProfile.current = profile;
        setUser(profile);
        setError("");
      } catch (error) {
        if (!recovered && error instanceof ViewerError && error.status === 401) {
          resetDeviceAccount(); resetAnalytics();
          if (mounted.current) setUser(null);
          refreshTask.current = null;
          return await refresh(true, true);
        }
        if (mounted.current) fail(error);
      } finally {
        if (mounted.current) setReady(true);
        refreshTask.current = null;
      }
    })();
    return refreshTask.current;
  }
  useEffect(() => {
    mounted.current = true;
    void refresh(false, true).then(() => {
      void initializeAnalytics();
    });
    const focus = () => {
      void refresh();
    };
    addEventListener("focus", focus);
    addEventListener("online", focus);
    const accountResult = async (event: MessageEvent) => {
      if (window.parent === window || event.origin !== frameParentOrigin() || event.source !== window.parent || event.data?.type !== "yestv:account-result" || !embeddedLogin.current || event.data.requestId !== embeddedLogin.current || typeof event.data.success !== "boolean") return;
      if (!event.data.success) { finishLogin(false); return; }
      resetDeviceAccount(); resetAnalytics();
      if (refreshTask.current) await refreshTask.current;
      await refresh();
      if (mounted.current) finishLogin(currentProfile.current?.registered === true);
    };
    addEventListener("message", accountResult);
    const installResult = (event: MessageEvent) => {
      if (window.parent === window || event.origin !== frameParentOrigin() || event.source !== window.parent || event.data?.type !== "yestv:install-result" || !embeddedInstall.current || event.data.requestId !== embeddedInstall.current) return;
      finishInstall();
    };
    addEventListener("message", installResult);
    const accountChanged = async (event: MessageEvent) => {
      if (window.parent === window || event.origin !== frameParentOrigin() || event.source !== window.parent || event.data?.type !== "yestv:account-changed") return;
      if (refreshTask.current) await refreshTask.current;
      resetDeviceAccount(); resetAnalytics(); await refresh();
    };
    addEventListener("message", accountChanged);
    try { authChannel.current = new BroadcastChannel("yestv-account"); authChannel.current.onmessage = () => { resetDeviceAccount(); resetAnalytics(); void refresh(); }; } catch { /* Focus refresh remains available. */ }
    return () => {
      mounted.current = false;
      removeEventListener("focus", focus);
      removeEventListener("online", focus);
      removeEventListener("message", accountResult);
      removeEventListener("message", installResult);
      removeEventListener("message", accountChanged);
      loginWaiters.current.splice(0).forEach(resolve => resolve(false));
      installWaiters.current.splice(0).forEach(resolve => resolve());
      authChannel.current?.close();
    };
  }, []);
  useEffect(() => {
    if (!installOpen) return;
    installDialog.current?.showModal();
    const mode = matchMedia("(display-mode: standalone)");
    const installed = () => { if (isHomeScreen()) finishInstall(); };
    mode.addEventListener("change", installed); addEventListener("appinstalled", finishInstall); addEventListener("pageshow", installed); addEventListener("focus", installed); document.addEventListener("visibilitychange", installed);
    return () => { installDialog.current?.close(); mode.removeEventListener("change", installed); removeEventListener("appinstalled", finishInstall); removeEventListener("pageshow", installed); removeEventListener("focus", installed); document.removeEventListener("visibilitychange", installed); };
  }, [installOpen]);
  function finishInstall() { dismissInstallForSession(); embeddedInstall.current = null; installActive.current = false; setInstallOpen(false); installWaiters.current.splice(0).forEach(resolve => resolve()); }
  function requestInstall(reason: InstallReason = "manual"): Promise<void> {
    if (isHomeScreen()) return Promise.resolve();
    if (window.parent === window && !installActive.current && reason === "trial" && (!getInstallContext().ios || !shouldOfferInstall())) return Promise.resolve();
    return new Promise(resolve => {
      installWaiters.current.push(resolve);
      if (window.parent !== window) {
        if (embeddedInstall.current) return;
        embeddedInstall.current = crypto.randomUUID();
        window.parent.postMessage({ type: "yestv:install", reason, requestId: embeddedInstall.current }, frameParentOrigin());
      } else {
        if (installActive.current) return;
        installActive.current = true; track("install_prompt"); setInstallReason(reason);
        setInstallOpen(true);
      }
    });
  }
  useEffect(() => {
    if (installIntentHandled.current || window.parent !== window) return;
    const url = new URL(location.href), context = getInstallContext();
    if (url.searchParams.get("install") !== "1" || !context.ios || context.handoff) return;
    installIntentHandled.current = true; url.searchParams.delete("install");
    history.replaceState(null, "", url.pathname + url.search + url.hash);
    if (!isHomeScreen()) void requestInstall("manual");
  }, [user?.userId]);
  function finishLogin(value: boolean) { embeddedLogin.current = null; setLoginReason(null); const pending = loginWaiters.current.splice(0); pending.forEach(resolve => resolve(value)); }
  async function requestLogin(reason: LoginReason = "login"): Promise<boolean> {
    if (!currentProfile.current) await refresh();
    if (!currentProfile.current) return false;
    if (currentProfile.current.registered) return true;
    if (window.parent !== window) {
      return new Promise(resolve => {
        loginWaiters.current.push(resolve);
        if (embeddedLogin.current) return;
        embeddedLogin.current = crypto.randomUUID();
        window.parent.postMessage({ type: "yestv:account", action: "login", reason, requestId: embeddedLogin.current }, frameParentOrigin());
      });
    }
    try {
      setLoginLoading(true);
      const module = await loadLogin();
      if (!mounted.current) return false;
      if (currentProfile.current?.registered) return true;
      setLoginPanel(() => module.default);
      setError("");
    } catch {
      if (mounted.current) setError("登录窗口暂时无法打开，请重试。");
      return false;
    } finally { if (mounted.current) setLoginLoading(false); }
    setLoginReason(current => current || reason);
    return new Promise(resolve => loginWaiters.current.push(resolve));
  }
  function offerRegistration() {
    if (user?.registered || loginReason || offered.current) return;
    offered.current = true;
    const key = `yestv:registration-offered:${user?.userId || "guest"}`;
    try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, "1"); } catch { /* Once per mounted viewer fallback. */ }
    void requestLogin("trial");
  }
  async function signedIn(result: LoginResult) {
    resetDeviceAccount(); if (refreshTask.current) await refreshTask.current; await refresh();
    finishLogin(true); authChannel.current?.postMessage("signed-in");
    if (result.newRegistration) {
      const id = currentProfile.current?.userId;
      try {
        const module = await import("./PostRegistration");
        if (mounted.current && currentProfile.current?.userId === id && currentProfile.current?.registered) {
          setSuccessPanel(() => module.default); setRegisteredNotice(true);
        }
      } catch { /* Registration is already complete; the account remains usable. */ }
    }
  }
  async function logout() {
    await deviceRequest("account/logout", "POST", {});
    resetDeviceAccount(); resetAnalytics(); setRegisteredNotice(false);
    authChannel.current?.postMessage("signed-out");
    if (refreshTask.current) await refreshTask.current; await refresh(); await initializeAnalytics();
  }
  async function updateProfile(nickname: string, avatar: string) {
    if (!user) throw new Error(t("Could not connect to your account."));
    const profile = await deviceRequest<Viewer>("account/me", "PUT", {
      nickname,
      avatar,
      expectedRevision: user.revision,
    });
    if (mounted.current) {
      setUser(profile);
      setError("");
    }
  }
  const changeItem = (change: Change) => changeLocalItem(change);
  return (
    <Context.Provider
      value={{
        user,
        ready,
        error,
        guestId: user?.userId || "",
        items,
        refresh,
        updateProfile,
        changeItem,
        requestLogin,
        requestInstall,
        offerRegistration,
        logout,
      }}
    >
      {user || publicContent ? (
        <>{children}{loginLoading && <p className="viewer-action-loading" role="status">正在打开登录…</p>}{installOpen && <dialog ref={installDialog} className="portal-dialog apple-install-dialog" aria-labelledby="portal-dialog-title" onCancel={finishInstall}><div className="dialog-top"><button className="round-button" onClick={finishInstall} aria-label={installReason === "trial" ? "关闭添加桌面引导" : t("Close dialog")}>×</button></div><InstallGuide onClose={finishInstall} reason={installReason} /></dialog>}{loginReason && LoginPanel && <LoginPanel reason={loginReason} onClose={() => finishLogin(false)} onSuccess={signedIn} />}{registeredNotice && SuccessPanel && <SuccessPanel onClose={() => setRegisteredNotice(false)} />}</>
      ) : (
        <main className="device-account-gate" aria-live="polite">
          <a href="/" className="wordmark">
            yes<span>tv</span>
            <i>.</i>
          </a>
          <h1>
            {t(
              ready
                ? "Account connection unavailable"
                : "Preparing your account…",
            )}
          </h1>
          <p>
            {error ||
              t(
                "正在准备观看页面，你可以先以游客身份试看。",
              )}
          </p>
          {ready && (
            <button
              className="account-primary"
              onClick={() => {
                setReady(false);
                void refresh();
              }}
            >
              {t("Retry connection")}
            </button>
          )}
        </main>
      )}
    </Context.Provider>
  );
}
